// AiAssistantContext — the panel's whole public surface, in one value.
//
// ─── WHY THE DEFAULT IS INERT AND NOT `undefined` ──────────────────────────
// `useAiAssistant()` outside `<AiAssistantProvider>` returns a no-op value
// rather than throwing. That is a direct lesson from `FeatureFlagProvider`,
// which is mounted nowhere in this app: its `useFlag` throws, so the first
// component that reached for a flag would have taken the whole CRM down at
// import-time rather than degrading.
//
// The assistant is a sidecar. A page that renders `AiAssistantLauncher` while
// the provider is missing (a unit test, a Storybook-style harness, a future
// route mounted outside the shell) must render a dead button, never a blank
// screen. Everything in `INERT_AI_ASSISTANT` is therefore safe to call and
// does nothing; `canAsk` is `false`, so nothing downstream even offers to
// send.

import { createContext, useContext } from 'react';

import type { AiConversation } from '../types';

/**
 * A request to load text into the composer without sending it.
 *
 * `nonce` is load-bearing. The suggested-prompt chips can be clicked twice with
 * the same text, and a plain string would compare equal and the effect that
 * moves the caret would not re-run. The composer watches the nonce.
 */
export interface AiComposerPrefill {
  readonly text: string;
  readonly nonce: number;
}

/**
 * What the visually-hidden live region should say right now.
 *
 * Transitions only — never answer bodies. See `AiLiveRegion`.
 */
export interface AiLiveAnnouncement {
  readonly text: string;
  readonly level: 'polite' | 'assertive';
}

export interface AiAssistantContextValue {
  // ── visibility ──
  readonly isOpen: boolean;
  /** Width preference for the DOCKED panel only. Persisted per session. */
  readonly isWide: boolean;

  // ── conversation ──
  readonly conversation: AiConversation;
  /**
   * True while an ask is in flight.
   *
   * Derived from the presence of a `pending` turn, never stored: a second
   * `useState<LoadState>` beside the reducer is exactly how "Send" and the
   * spinner end up disagreeing.
   */
  readonly isPending: boolean;
  /** Whether anything has been sent yet — the composer hint hides after the first. */
  readonly hasSent: boolean;

  // ── tenant safety ──
  /** The live session tenant, or `null`. NEVER an env fallback. */
  readonly sessionTenantId: string | null;
  /** False when there is no tenant: the composer refuses and no request is issued. */
  readonly canAsk: boolean;
  /** True once a tenant change has thrown away a conversation that had turns. */
  readonly tenantDiscarded: boolean;

  // ── cross-component signals ──
  readonly prefill: AiComposerPrefill | null;
  readonly live: AiLiveAnnouncement;
  /** Set while a reply landed with the panel closed — the launcher shows a dot. */
  readonly answerReadyWhileClosed: boolean;
  /**
   * Bumped when the panel closed and the element that had focus before it
   * opened is gone. The launcher focuses itself on a change.
   */
  readonly focusLauncherNonce: number;

  // ── commands ──
  open(): void;
  close(): void;
  toggle(): void;
  setWide(wide: boolean): void;
  ask(text: string): void;
  cancel(): void;
  retry(text: string): void;
  prefillComposer(text: string): void;
  /**
   * Back to the suggested questions.
   *
   * ⚠️ DISCARDS the conversation — that is what "back to the start screen"
   * means here, because the suggestions only render when the log is empty.
   * The caller is expected to gate this on there being turns to discard, and
   * to label it so an operator knows the answer goes away.
   */
  backToSuggestions(): void;
}

const EMPTY_CONVERSATION: AiConversation = { id: '', tenantId: '', turns: [] };

function noop(): void {
  /* inert — see the file header */
}

/** The value `useAiAssistant()` returns with no provider above it. */
export const INERT_AI_ASSISTANT: AiAssistantContextValue = {
  isOpen: false,
  isWide: false,
  conversation: EMPTY_CONVERSATION,
  isPending: false,
  hasSent: false,
  sessionTenantId: null,
  canAsk: false,
  tenantDiscarded: false,
  prefill: null,
  live: { text: '', level: 'polite' },
  answerReadyWhileClosed: false,
  focusLauncherNonce: 0,
  open: noop,
  close: noop,
  toggle: noop,
  setWide: noop,
  ask: noop,
  cancel: noop,
  retry: noop,
  prefillComposer: noop,
  backToSuggestions: noop,
};

export const AiAssistantContext =
  createContext<AiAssistantContextValue>(INERT_AI_ASSISTANT);

export function useAiAssistant(): AiAssistantContextValue {
  return useContext(AiAssistantContext);
}
